import { SimpleGrid, Spoiler } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { usePartFeatures } from "#modules/part/view/usePartFeatures";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import { AnalyzeError } from "#modules/quoteRequestLines/AnalyzeError";
import { AnalyzeLoader } from "#modules/quoteRequestLines/AnalyzeLoader";
import { Feature } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/Features/Feature";
import { UpdateFeaturesModal } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/Features/UpdateFeaturesModal";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { Icon, IconName } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { TextButton } from "#shared/view/designSystem/TextButton";
import {
  isPartTypeCompatibleWithFeaturesDetection,
  PartTypeType,
  VisibleFeatureType,
} from "shared";

type Props = {
  partId: string;
  partType: PartTypeType | null;
  has2DPrediction?: boolean;
  displayMode?: "all" | "truncate";
  title?: string;
  cols?: number;
  editable?: boolean;
};

const featureIconLut: Record<VisibleFeatureType, IconName> = {
  Hole: "circle",
  Folding: "vectorBezier2",
  Side: "frame",
};

const THREE_LINES_IN_PIXELS = 60;
const MAX_STACKED_FEATURES = 3;

export const Features = ({
  partId,
  displayMode = "all",
  title,
  partType,
  cols = 1,
  editable = false,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [areFeaturesExpanded, setAreFeaturesExpanded] = useState(displayMode === "all");
  const [isEditModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const { features, hasUserOverridden } = usePartFeatures(partId);

  const { updatePartAsync, isPending: isUpdatingPart } = useUpdatePart(partId);

  const resetFeatures = () => {
    void updatePartAsync({
      id: partId,
      payload: { formattedFeaturesJsonOverride: null },
    });
  };

  const { checkPart } = useCheckPart({
    onMutate: () => {
      setIsFetching(true);
    },
  });

  if (!partId) {
    return null;
  }

  const relaunchAnalyze = () => {
    checkPart({
      id: partId,
      analyzeType: "COMPUTE_FEATURES",
      force: true,
      assemblySplitMode: "ROOT",
    });
  };

  if (isFetching) {
    return <AnalyzeLoader text={t("qrl.analyze.analyzing")} />;
  }

  const isFeaturesEmpty =
    features.length === 0 && isPartTypeCompatibleWithFeaturesDetection(partType);

  if (isFeaturesEmpty && !hasUserOverridden) {
    return (
      <AnalyzeError
        retryAction={relaunchAnalyze}
        retryText={t("qrl.analyze.relaunch")}
        errorText={t("qrl.panel.features.noFeatures")}
        justify="start"
      />
    );
  }

  return (
    <Stack gap="0" {...props}>
      {title && (
        <Group justify="start" gap="xs" wrap="nowrap">
          <Text fw={700} c={colors.violet[5]}>
            {title}
          </Text>
          {editable && (
            <ActionIcon
              size="xs"
              variant="subtle"
              c="dimmed"
              aria-label={t("part.features.edit")}
              onClick={openEditModal}
            >
              <Icon name="pencil" size={16} color={colors.violet[5]} />
            </ActionIcon>
          )}
        </Group>
      )}
      <Spoiler
        expanded={areFeaturesExpanded}
        hideLabel={null}
        showLabel={null}
        maxHeight={THREE_LINES_IN_PIXELS}
        onExpandedChange={() => setAreFeaturesExpanded((prev) => !prev)}
        display={displayMode !== "all" ? "hidden" : "visible"}
      >
        <SimpleGrid
          cols={features.length > MAX_STACKED_FEATURES ? cols : 1}
          spacing="xs"
          verticalSpacing="0"
        >
          {features.length > 0 ? (
            features.map((feature, index) => {
              return <Feature key={index} feature={feature} icon={featureIconLut[feature.type]} />;
            })
          ) : (
            <Button
              onClick={resetFeatures}
              loading={isUpdatingPart}
              disabled={isUpdatingPart}
              variant="light"
              w="300px"
            >
              {t("part.features.resetFeatures")}
            </Button>
          )}
        </SimpleGrid>
      </Spoiler>
      {displayMode !== "all" && (
        <TextButton
          onClick={(e) => {
            e.stopPropagation();
            setAreFeaturesExpanded((prev) => !prev);
          }}
        >
          {areFeaturesExpanded ? t("part.features.seeLess") : t("part.features.seeMore")}
        </TextButton>
      )}
      {editable && partId && (
        <UpdateFeaturesModal
          partId={partId}
          features={features}
          opened={isEditModalOpened}
          onClose={closeEditModal}
        />
      )}
    </Stack>
  );
};
